/* istanbul ignore file */
import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import styled from 'styled-components';
import { ReactWindowList } from './reactWindowList';
import { SimpleDropdown } from './simpleDropdown';

const ButtonWithOutline = styled.button`
    &:focus {
        // for testing - to show outline
        // outline: auto 5px -webkit-focus-ring-color;
    }
`;

export class Dropdown extends React.Component {
    static displayName = 'Dropdown';

    static propTypes = {
        // Supports both array and object as listOptions, but it converts the object to an array before rendering.
        // If the item order in the dropdown is important, provide array since object iteration
        // doesn't guarantee the order
        listOptions: PropTypes.oneOfType([
            PropTypes.object.isRequired,
            PropTypes.array.isRequired
        ]),
        listClassName: PropTypes.string,
        // Unique key for retrieving row data in table
        recordKey: PropTypes.string,

        onSelect: PropTypes.func.isRequired,

        //optional props
        isGridLayout: PropTypes.bool,
        buttonClassName: PropTypes.string,
        buttonOpenClassName: PropTypes.string,
        caretOpenClassName: PropTypes.string,
        caretClassName: PropTypes.string,
        buttonLabel: PropTypes.oneOfType([
            PropTypes.object,
            PropTypes.string
        ]),
        buttonTitle: PropTypes.string,
        className: PropTypes.string,
        disableCaret: PropTypes.bool,
        disabled: PropTypes.bool,
        forceClosed: PropTypes.bool,
        id: PropTypes.string,
        isSearchable: PropTypes.bool,
        itemClassName: PropTypes.string,
        labelClassName: PropTypes.string,
        placeHolderLabel: PropTypes.string,

        onDropdownOpen: PropTypes.func,
        onDropdownClose: PropTypes.func,
        itemRenderer: PropTypes.func,

        // Props coming from search dropdown
        SearchInput: PropTypes.func, // React class to be used for search input
        filterListOptions: PropTypes.func,

        // Pass this class name to the parent simpleDropdown
        simpleClassName: PropTypes.string,


        value: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
            PropTypes.bool,
        ]),

        // for keyboard navigation - all optional
        cursor: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
            PropTypes.object,
        ]),
        onHovered: PropTypes.func,
        columnStyles: PropTypes.object,
    };

    static defaultProps = {
        onHovered: _.noop,
    };

    render() {
        let {
            listClassName,
            isGridLayout,
            buttonLabel,
            buttonClassName = 'tw-border tw-border-gray-300 dark:tw-bg-gray-700 dark:tw-border dark:tw-border-gray-700',
            buttonOpenClassName = 'tw-border tw-border-gray-300 dark:tw-text-white dark:tw-bg-gray-700 dark:tw-border dark:tw-border-gray-500',
            className,
            disabled,
            id,
            labelClassName,
            placeHolderLabel,
            simpleClassName,
            forceClosed,
            buttonTitle
        } = this.props;
        buttonLabel = buttonLabel || this.getValue() || placeHolderLabel || '';
        const buttonToolTip = buttonTitle || (_.isString(buttonLabel) ? buttonLabel : undefined);

        let additionalButtonOptions = {};

        if (isGridLayout) {
            if (_.get(buttonLabel, 'props.title')) {
                additionalButtonOptions['title'] = buttonLabel.props.title;
            }
        }

        return (
            <SimpleDropdown
                className={simpleClassName}
                disabled={disabled}
                forceClosed={forceClosed}
                onDropdownOpen={this.onDropdownOpen}
                onDropdownClose={this.onDropdownClose}>
                {(isOpen) => (  
                    <div ref="btnGroup" className={classnames('dropdown', className, {'open': isOpen})}>
                        <ButtonWithOutline {...additionalButtonOptions}
                            id={id}
                            className={classnames('dropdown-toggle', buttonClassName, isOpen ? buttonOpenClassName : '')}
                            data-click-close
                            type="button"
                            title={buttonToolTip}
                            disabled={disabled}>
                            <span className={classnames('dropdown-label', labelClassName)}>{buttonLabel}</span>
                            {this.showDropdownCaret(isOpen)}
                        </ButtonWithOutline>
                        <div
                            id={id ? `${id}-menu` : undefined}
                            className={classnames('dropdown-menu scrollable-menu-parent dark:tw-bg-gray-800 dark:tw-border-gray-700 dark:tw-shadow-dark-gray-md', listClassName, {'grid-layout': isGridLayout})}>
                            <div>
                                <div className="droplist-panel form">
                                    { this.showDropdownSearchInput(isOpen, id)}
                                    { this.renderDropdownList(isOpen) }
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </SimpleDropdown>
        );
    }

    renderDropdownList = (isOpen) => {
        if (!isOpen)
            return;

        const { id, itemClassName, isGridLayout, onHovered, cursor } = this.props;
        let { listOptions } = this.props;

        if (!_.isArray(listOptions)) {
            listOptions = _.map(listOptions, (label, key) => {
                return { key, label };
            });
        }
        listOptions = !_.isUndefined(this.props.filterListOptions) ? this.props.filterListOptions(listOptions) : listOptions;

        if (isGridLayout) {
            const styledListOptions = [];
            _.forEach(listOptions, (value, i) => {
                if (i % 5 === 0 && i !== 0) {
                    styledListOptions.push({'key': '_NEXT_ROW'});
                }
                styledListOptions.push(value);
            });
            listOptions = styledListOptions;
        }

        let lastGroup;
        let columnStyles = this.props.columnStyles || {};

        if (_.size(listOptions) > 200 && !isGridLayout) {
            const listOptionsWithGroupName = this.listOptionsWithGroup(listOptions);
            return (
                <ReactWindowList
                    id={id}
                    maxWidth={500}
                    listOptions={listOptionsWithGroupName}
                    itemsRenderer={this.reactWindowItemsRenderer}
                    onToggleItem={(key) => this.props.onSelect(key, this.props.recordKey)}
                    onHovered={onHovered}
                    cursor={cursor} />
            );
        }

        return (
            <ul className={`droplist-items scrollable-menu js-navigable-menu-container-${_.kebabCase(id)}`}>
                {_.map(listOptions, (value, inx) => {
                    // Adding showTitle for suppressing text shown for the list items (DisplayQueryType component) when the cursor is over them.
                    // default value for showTitle will always be true since we need text to be shown when the cursor is over the list items.
                    const { key, icon, label, tag, disabled, _groupName, title = key, showTitle = true } = value;
                    let liItems = [];
                    
                    if (!_.isNil(_groupName) && _groupName !== lastGroup) {
                        if (!_.isNil(lastGroup)) {
                            liItems.push( <li className="divider" key={key + inx}/> );
                        }
                        liItems.push( <li className="dropdown-heading" key={_groupName + inx}>{_groupName}</li> );
                        lastGroup = _groupName;
                    }
                    if (key === '_NEXT_ROW') {
                        liItems.push( <li className="next-row" key={key + inx}/> );
                    }
                    else if (key === '_LIST_SEPARATOR') {
                        if (inx === 0 || inx === listOptions.length - 1 || listOptions[inx + 1].key === '_LIST_SEPARATOR')
                            return;
                        liItems.push( <li className="divider" key={key + inx}/> );
                    }
                    else {
                        liItems.push(
                            <li 
                                title={showTitle && title}
                                key={key}
                                className={
                                    classnames(
                                        'dark:hover:tw-bg-gray-700',
                                        itemClassName,
                                        columnStyles[key],
                                        {disabled: disabled},
                                        `js-droplist-item-${_.kebabCase(key)}`,
                                        `js-navigable-item-${_.kebabCase(id)}-${inx}`,
                                        {'tw-bg-gray-200-override dark:tw-bg-gray-700': inx === cursor}
                                    )
                                }
                                onMouseEnter={() => onHovered(inx)}
                                onMouseLeave={() => onHovered(undefined)}
                                data-click-close={!disabled}
                                data-record-key={this.props.recordKey}
                                onClick={disabled ? null : (e) => this.onSelect(e, key)}>
                                <a id={`a-${_.kebabCase(key)}`} className="select-counter" href="#" onClick={(e) => e.preventDefault()}>
                                    {this.props.itemRenderer ? this.props.itemRenderer(label, icon) : <span className={'flex-row flex-align-center'}>{icon}{label}{tag}</span>}
                                </a>
                            </li>
                        );
                    }
                    return liItems;
                })}
            </ul>
        );
    };

    // a special renderer for react window list, it handles group names differently, and it will not have a list separator
    // between option groups instead has a group heading with separator.
    reactWindowItemsRenderer = ({data, index, style}) => {
        const { key, icon, label, tag, disabled, _groupName, title = key } = data;
        const { id, itemClassName, onHovered, cursor } = this.props;

        if(key === '_GROUP_HEADING') {
            return <li className="dropdown-heading" key={_groupName + index} style={style}>{_groupName}</li>;
        }
        else if(key === '_NEXT_ROW') {
            return <li className="next-row" key={key + index} style={style}/>;
        }
        else if (key === '_GROUP_HEADING_WITH_SEPARATOR') {
            return <li className="dropdown-heading tw-border-t tw-border-gray-200 dark:tw-border-gray-700" key={_groupName + index} style={style}>{_groupName}</li>;
        }
        else {
            return (
                <li 
                    key={key}
                    title={title}
                    style={style}
                    className={
                        classnames(
                            'dark:hover:tw-bg-gray-700',
                            itemClassName,
                            {disabled: disabled},
                            `js-droplist-item-${_.kebabCase(key)}`,
                            `js-navigable-item-${_.kebabCase(id)}-${index}`,
                            {'tw-bg-gray-100 dark:tw-bg-gray-700': index === cursor}
                        )
                    }
                    onMouseEnter={() => onHovered(index)}
                    onMouseLeave={() => onHovered(undefined)}
                    data-click-close={!disabled}
                    data-record-key={this.props.recordKey}
                    onClick={disabled ? null : (e)=> this.onSelect(e, key)}>
                    <a id={`a-${_.kebabCase(key)}`} className="select-counter" href="#" onClick={(e) => e.preventDefault()}>
                        {this.props.itemRenderer ? this.props.itemRenderer(label, icon): <span className={'flex-row flex-align-center'}>{icon}{label}{tag}</span>}
                    </a>
                </li>
            );
        }
    };

    listOptionsWithGroup = (listOptions) => {
        let lastGroup;
        let listOptionsWithGroupName = [];
        _.map(listOptions, (value) => {
            const { _groupName } = value;
            if (!_.isNil(_groupName) && _groupName !== lastGroup) {
                const key = _.isNil(lastGroup) ? '_GROUP_HEADING' : '_GROUP_HEADING_WITH_SEPARATOR';
                listOptionsWithGroupName.push({ key, label: _groupName, disabled:false, _groupName:_groupName });
                lastGroup = _groupName;
            }
            listOptionsWithGroupName.push(value);
        });
        return listOptionsWithGroupName;
    };

    onSelect = (e, key) => {
        const recordKey = e.currentTarget.getAttribute('data-record-key');
        this.props.onSelect(key, recordKey);
    };

    showDropdownSearchInput = (isOpen, id) => {
        if (!_.isUndefined(this.props.SearchInput)) {
            return (
                <this.props.SearchInput isOpen={isOpen} id={id}/>
            );
        }
    };

    showDropdownCaret = (isOpen = false) => {
        let {
            disableCaret,
            caretOpenClassName = 'dark:tw-text-white',
            caretClassName = 'dark:tw-text-gray-500'
        } = this.props;
        
        if (!disableCaret) {
            return (
                <span className={classnames('caret', isOpen ? caretOpenClassName : caretClassName)} />
            );
        }
    };

    onDropdownOpen = (closeDropdown) => {
        this._closeDropdown = closeDropdown;
        if (this.props.onDropdownOpen) {
            this.props.onDropdownOpen(this);
        }
    };

    closeDropdown = () => {
        _.isFunction(this._closeDropdown) && this._closeDropdown();
    };

    onDropdownClose = () => {
        if (this.props.onDropdownClose) {
            this.props.onDropdownClose(this);
        }
    };

    getValue = () => {
        let value = _.isNil(this.props.value) ? '' : this.props.value; // use empty string for _.isNil()
        return _.get(_.find(this.props.listOptions, {key: value}), 'label', '');
    };
}
